// src/utils/editorSerialize.js
//
// Single serialization boundary for the editor's contentEditable host.
//
// The editor's source of truth is `editorRef.current.innerHTML`, which is
// captured on every keystroke and then (a) saved to the server, (b) broadcast
// live to collaborators, and (c) exported to PDF. Any view-only DOM that we
// inject for display purposes — e.g. "Show Non-Printing Characters" markers —
// must NEVER end up in that serialized HTML.
//
// To guarantee that by construction (rather than by remembering to strip at
// each call site), every persistence / broadcast / export path reads the
// editor through `getCleanHTML()`. View-only nodes are tagged with
// DECORATION_ATTR and removed from a CLONE here, so the live DOM is never
// mutated and the stored document stays clean regardless of timing.

// Additive, content-free markers (e.g. the line-break ↵ glyph). These hold no
// real document text, so they are removed wholesale on serialization.
export const DECORATION_ATTR = 'data-rv-decoration';

// Whitespace wrappers. Each wraps a SINGLE real character (space, tab, nbsp)
// so a glyph can be drawn over it. The wrapper still contains the literal
// character, so on serialization it is UNWRAPPED (replaced by that character)
// rather than removed — removing it would delete real text. The attribute's
// value is the exact character it represents.
export const WS_ATTR = 'data-rv-ws';

// View-only highlight wrappers (comment highlights, collaborator-edit
// highlights). Each wraps a RUN of real document text to tint it. Like the
// whitespace wrapper, it contains real content, so on serialization it is
// UNWRAPPED (children moved out, wrapper removed) rather than deleted. The
// attribute's value names the kind ("comment" | "collab") for debugging.
export const HIGHLIGHT_ATTR = 'data-rv-highlight';

// Prefix of the tooltip collabHighlights writes onto a decorated block
// ("Edited by Ada", "Edited by Ada & Bob"). Declared HERE rather than in
// collabHighlights so this module can strip the tooltip without importing from
// a module that already imports from it.
export const COLLAB_LABEL_PREFIX = 'Edited by ';

/**
 * Return the editor host's HTML with all view-only decorations normalized away.
 * Operates on a clone — never mutates the live DOM, so it is safe to call from
 * hot paths (autosave, collab emit, export) at any time.
 *
 *   - [DECORATION_ATTR] nodes are removed (they carry no real content).
 *   - [WS_ATTR] nodes are unwrapped back to their literal character (lossless).
 *   - [HIGHLIGHT_ATTR] nodes are unwrapped to their children (lossless) — the
 *     tint wrapper disappears but the highlighted text is preserved.
 *
 * @param {Element|null|undefined} el contentEditable host element
 * @returns {string} clean serialized HTML (empty string if no element)
 */
export function getCleanHTML(el) {
  if (!el) return '';
  const clone = el.cloneNode(true);
  clone.querySelectorAll(`[${DECORATION_ATTR}]`).forEach((n) => n.remove());
  clone.querySelectorAll('.rv-page-delete-btn').forEach((n) => n.remove());
  clone.querySelectorAll(`[${WS_ATTR}]`).forEach((span) => {
    span.replaceWith(document.createTextNode(span.getAttribute(WS_ATTR) ?? ''));
  });
  // Pilcrow font vars (--rv-mark-size/--rv-mark-font, set by
  // applyFormattingMarks so the ¶ glyph matches the paragraph's text font)
  // are view-only and must not leak into saved HTML.
  clone.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6').forEach((block) => {
    block.style.removeProperty('--rv-mark-size');
    block.style.removeProperty('--rv-mark-font');
    if (block.style.length === 0) block.removeAttribute('style');
  });
  // Strip highlight markers. Any <span> carrying the attr is a wrapper (comment
  // highlights, or per-word collab segments) — unwrap its children. Collab
  // BLOCK-mode highlights live as attrs/styles on block elements (<p>, <h1>…) —
  // remove the attribute and inline styles, keeping the element itself.
  clone.querySelectorAll(`[${HIGHLIGHT_ATTR}]`).forEach((el) => {
    if (el.tagName === 'SPAN') {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    } else {
      el.removeAttribute(HIGHLIGHT_ATTR);
      el.style.background = '';
      el.style.backgroundColor = '';
      el.style.borderLeft = '';
      el.style.borderImage = '';
      el.style.paddingLeft = '';
      el.removeAttribute('title');
      el.classList.remove('rv-collab-highlight');
    }
  });

  // Belt and braces. The cleanup above only reaches elements still carrying
  // HIGHLIGHT_ATTR — but a block can lose that marker while keeping the
  // tooltip, and once that happens nothing ever removes it. Decoration titles
  // were found welded into saved manuscripts this way, where they would follow
  // the text into exports and published books. Any title with the decoration's
  // prefix is view-only by definition, so strip it wherever it turns up.
  clone.querySelectorAll('[title]').forEach((el) => {
    const t = el.getAttribute('title') || '';
    if (t === '' || t.startsWith(COLLAB_LABEL_PREFIX)) el.removeAttribute('title');
  });
  // Drop the empty attributes left behind by removing a decoration's only class
  // or title, so saved HTML doesn't accumulate class="" title="" litter.
  clone.querySelectorAll('[class=""]').forEach((el) => el.removeAttribute('class'));
  // Unwrapping segment spans can leave the block split into several adjacent
  // text nodes; merge them so the serialized text is identical to the original.
  clone.normalize();
  return clone.innerHTML;
}
